import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { Button, Card, message, Select, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { post } from 'core/fetch';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

const { Title } = Typography;

interface ApiResponse<T> {
  valid: boolean;
  errorMessage?: string;
  data?: T;
}

interface ClassOption {
  id: number;
  name: string;
  class_number: number | null;
}

interface StudentOption {
  id: number;
  full_name: string;
  code: string;
  card_balance: number;
}

interface TransactionRow {
  id: number;
  transaction_type: 'credit' | 'debit';
  amount: number;
  discount_amount: number;
  paid_amount: number;
  channel: string | null;
  description: string | null;
  transaction_date: string;
  service_date: string;
}

interface StudentSummary {
  student: StudentOption;
  transactions: TransactionRow[];
}

function formatMNT(n: number): string {
  return new Intl.NumberFormat('mn-MN').format(n) + '₮';
}

function formatDate(value: string): string {
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function StatCard({
  label,
  value,
  color,
  iconColor,
  icon,
}: {
  label: string;
  value: string;
  color?: string;
  iconColor?: string;
  icon?: ReactNode;
}) {
  return (
    <Card styles={{ body: { padding: '20px 24px' } }} style={{ minWidth: 220, borderRadius: 12 }}>
      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, marginBottom: 8 }}>{label}</div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 26,
          fontWeight: 600,
          color: color ?? 'rgba(0, 0, 0, 0.88)',
        }}
      >
        {icon && <span style={{ color: iconColor ?? color }}>{icon}</span>}
        {value}
      </div>
    </Card>
  );
}

const CardTransactionCalculation = () => {
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<number>();

  const [students, setStudents] = useState<StudentOption[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number>();

  const [summary, setSummary] = useState<StudentSummary | null>(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    setClassesLoading(true);
    post<ApiResponse<{ items: ClassOption[] }>>('/custom/card-transaction/classes', {})
      .then((response) => {
        if (!response.valid) {
          message.error(response.errorMessage ?? 'Алдаа гарлаа.');
          return;
        }
        setClasses(response.data?.items ?? []);
      })
      .finally(() => setClassesLoading(false));
  }, []);

  useEffect(() => {
    setSelectedStudentId(undefined);
    setSummary(null);

    if (!selectedClassId) {
      setStudents([]);
      return;
    }

    setStudentsLoading(true);
    post<ApiResponse<{ items: StudentOption[] }>>('/custom/card-transaction/students', {
      class_id: selectedClassId,
    })
      .then((response) => {
        if (!response.valid) {
          message.error(response.errorMessage ?? 'Алдаа гарлаа.');
          return;
        }
        setStudents(response.data?.items ?? []);
      })
      .finally(() => setStudentsLoading(false));
  }, [selectedClassId]);

  const totals = useMemo(() => {
    const transactions = summary?.transactions ?? [];
    const totalCredit = transactions
      .filter((t) => t.transaction_type === 'credit')
      .reduce((sum, t) => sum + t.paid_amount, 0);
    const totalDebit = transactions
      .filter((t) => t.transaction_type === 'debit')
      .reduce((sum, t) => sum + t.paid_amount, 0);
    return { totalCredit, totalDebit, balance: totalCredit - totalDebit };
  }, [summary]);

  const balanceMatched = summary ? totals.balance === summary.student.card_balance : false;

  const handleCalculate = () => {
    if (!selectedStudentId) return;

    setCalculating(true);
    post<ApiResponse<StudentSummary>>('/custom/card-transaction/student-summary', {
      student_id: selectedStudentId,
    })
      .then((response) => {
        if (!response.valid || !response.data) {
          message.error(response.errorMessage ?? 'Алдаа гарлаа.');
          return;
        }
        setSummary(response.data);
      })
      .finally(() => setCalculating(false));
  };

  const channelFilters = useMemo(() => {
    const channels = new Set<string>();
    (summary?.transactions ?? []).forEach((t) => channels.add(t.channel ?? '—'));
    return Array.from(channels).map((c) => ({ text: c, value: c }));
  }, [summary]);

  const columns: ColumnsType<TransactionRow> = [
    {
      title: 'Огноо',
      dataIndex: 'service_date',
      width: 160,
      render: (value: string) => (value ? formatDate(value) : '—'),
    },
    {
      title: 'Төрөл',
      dataIndex: 'transaction_type',
      width: 100,
      filters: [
        { text: 'Орлого', value: 'credit' },
        { text: 'Зарлага', value: 'debit' },
      ],
      onFilter: (value, record) => record.transaction_type === value,
      render: (value: string) => (value === 'credit' ? 'Орлого' : 'Зарлага'),
    },
    {
      title: 'Суваг',
      dataIndex: 'channel',
      filters: channelFilters,
      onFilter: (value, record) => (record.channel ?? '—') === value,
      render: (value: string | null) => value ?? '—',
    },
    { title: 'Хөнгөлөлт', dataIndex: 'discount_amount', align: 'right', render: formatMNT },
    {
      title: 'Төлсөн дүн',
      dataIndex: 'paid_amount',
      align: 'right',
      render: (value: number, record: TransactionRow) =>
        !value ? (
          <span
            style={{
              display: 'inline-block',
              padding: '0 7px',
              borderRadius: 4,
              fontSize: 12,
              lineHeight: '20px',
              color: '#3f8600',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
            }}
          >
            Үнэгүй
          </span>
        ) : (
          <span style={{ color: record.transaction_type === 'credit' ? '#3f8600' : '#cf1322' }}>
            {record.transaction_type === 'credit' ? '+' : '-'}
            {formatMNT(value)}
          </span>
        ),
    },
    { title: 'Тайлбар', dataIndex: 'description', render: (value: string | null) => value ?? '—' },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <Title level={4} style={{ marginBottom: 16 }}>
          Сурагчийн тооцоо
        </Title>

        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginBottom: 24,
            flexWrap: 'wrap',
          }}
        >
          <Select
            placeholder="Анги сонгох"
            style={{ width: 240 }}
            loading={classesLoading}
            value={selectedClassId}
            onChange={(value) => setSelectedClassId(value)}
            allowClear
            onClear={() => setSelectedClassId(undefined)}
            options={classes.map((c) => ({
              value: c.id,
              label: c.class_number ? `${c.name} (${c.class_number}-р анги)` : c.name,
            }))}
          />

          <Select
            placeholder="Сурагч сонгох"
            style={{ width: 260 }}
            loading={studentsLoading}
            disabled={!selectedClassId}
            value={selectedStudentId}
            onChange={(value) => setSelectedStudentId(value)}
            allowClear
            onClear={() => setSelectedStudentId(undefined)}
            options={students.map((s) => ({
              value: s.id,
              label: `${s.full_name} (${s.code})`,
            }))}
          />

          <Button
            type="primary"
            disabled={!selectedStudentId}
            loading={calculating}
            onClick={handleCalculate}
          >
            Харах
          </Button>
        </div>

        {summary && (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
              <StatCard
                label="Нийт орлого"
                value={formatMNT(totals.totalCredit)}
                color="#3f8600"
                icon={<ArrowUpOutlined />}
              />
              <StatCard
                label="Нийт зарлага"
                value={formatMNT(totals.totalDebit)}
                color="#cf1322"
                icon={<ArrowDownOutlined />}
              />
              <StatCard label="Тооцоолсон үлдэгдэл" value={formatMNT(totals.balance)} />
              <StatCard
                label="Картын үлдэгдэл"
                value={formatMNT(summary.student.card_balance)}
                iconColor={balanceMatched ? '#3f8600' : '#cf1322'}
                icon={balanceMatched ? <CheckCircleFilled /> : <CloseCircleFilled />}
              />
            </div>

            <Table
              rowKey="id"
              columns={columns}
              dataSource={summary.transactions}
              pagination={{ pageSize: 15 }}
            />
          </>
        )}
      </Card>
    </LayoutWithMenu>
  );
};

export default CardTransactionCalculation;
